import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { formatApiError } from '../lib/api';
import logo from '../assets/logo.png';

export default function LoginPage() {
  const [username, setUsername] = useState(() => localStorage.getItem('cmic_remember_user') || '');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem('cmic_remember_user'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && user.role) {
      // Force password change overrides all other redirects.
      if (user.must_change_password) {
        navigate('/cambiar-contrasena', { replace: true });
        return;
      }
      const from = location.state?.from?.pathname;
      if (from && from !== '/login') {
        navigate(from, { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/es/mi-perfil', { replace: true });
      }
    }
  }, [user, navigate, location.state]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = username.trim();
      if (remember) localStorage.setItem('cmic_remember_user', u);
      else localStorage.removeItem('cmic_remember_user');
      await login(u, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Credenciales incorrectas. Verifica tu usuario o correo y tu contraseña.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex flex-col" data-testid="login-page">
      <div className="p-6">
        <Link to="/es" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 transition-colors" data-testid="login-back-link">
          <ArrowLeft size={16} />
          Volver al sitio
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <img src={logo} alt="CMIC" className="h-16 w-auto mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-charcoal-800">Acceso al Portal</h1>
            <p className="text-charcoal-500 mt-2 text-sm">Inicia sesión con tu usuario o correo y tu contraseña</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-charcoal-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-charcoal-700 mb-1.5">Usuario o correo electrónico</label>
                <input
                  id="username"
                  type="text"
                  required
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username-input"
                  className="w-full rounded-xl border border-charcoal-200 px-4 py-3 text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-cmic-500 focus:border-transparent transition-all"
                  placeholder="tu usuario o tu@correo.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-charcoal-700 mb-1.5">Contraseña</label>
                <div className="relative">
                  <input
                    id="password"
                    type={show ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="w-full rounded-xl border border-charcoal-200 px-4 py-3 pr-12 text-charcoal-800 placeholder:text-charcoal-300 focus:outline-none focus:ring-2 focus:ring-cmic-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShow(!show)}
                    data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-600 transition-colors">
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="login-error">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <><LogIn size={18} />Iniciar Sesión</>
                )}
              </button>

              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="login-remember-label">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="login-remember-checkbox"
                    className="h-4 w-4 rounded border-charcoal-300 text-cmic-600 focus:ring-cmic-500 cursor-pointer"
                  />
                  <span className="text-sm text-charcoal-600">Recordar usuario</span>
                </label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm text-cmic-600 hover:text-cmic-700 font-medium">¿Olvidaste tu contraseña?</Link>
              </div>
            </form>
          </div>

          <p className="text-center text-xs text-charcoal-400 mt-6">
            El acceso es solo para miembros del CMIC. Si recibiste una invitación, abre el enlace que te enviamos por correo para crear tu cuenta.
          </p>
        </div>
      </div>
    </div>
  );
}
